/**
 * Powód wpisu automatycznego w historii sprawy → klucz tłumaczenia + parametry.
 *
 * Serwer zapisuje przy decyzji bez osoby decydującej KOD powodu (np. „inna gałąź zakończyła sprawę"),
 * a panel składa z niego zdanie w języku użytkownika. Klucze są wypisane WPROST (nie sklejane
 * w locie), więc da się je wyszukać w kodzie; bramka `check:i18n` ich NIE sprawdza (skanuje tylko
 * literały w `t('…')`), a obecności tłumaczeń w pl.json i en.json pilnuje `system-reason.test.ts`.
 * `Record` po kodzie wymusza wpis dla każdego nowego kodu z `@saas/shared`.
 *
 * Powód przychodzi z kolumny jsonb i nie jest walidowany w czasie działania — nowy kod z serwera
 * przy starszej wersji panelu albo brak `params` nie mogą wywrócić tabeli: nieznany kod dostaje
 * tekst ogólny ({@link UNKNOWN_SYSTEM_REASON_I18N_KEY}).
 *
 * Kody eskalacji po terminie niosą identyfikator przełożonego (`params.supervisorUserId`); serwer
 * NIE zamraża jego nazwy — panel liczy ją tą samą drogą co nazwy w kolumnie „Kto" (funkcja
 * `userLabelOf` podana przez wywołującego), więc po zmianie imienia użytkownika historia pokazuje
 * aktualną nazwę. Bez identyfikatora w `params` (albo przy pustej nazwie z `userLabelOf`) zdanie
 * dostaje wariant bez nazwy.
 */

import {
  isProcessEscalationSystemReasonCode,
  PROCESS_DECISION_SYSTEM_REASON_CODES,
  type ProcessDecisionSystemReason,
  type ProcessDecisionSystemReasonCode,
  type ProcessEscalationSystemReasonCode,
} from '@saas/shared';

/**
 * Klucz tekstu po kodzie powodu. Dla kodów eskalacji to zdanie Z NAZWĄ przełożonego (miejsce
 * `{{supervisor}}`); wariant bez nazwy → {@link ESCALATION_UNNAMED_I18N_KEY}.
 */
export const SYSTEM_REASON_I18N_KEY: Record<ProcessDecisionSystemReasonCode, string> = {
  TARGET_NOT_FOUND: 'tenant.processes.history.systemReason.TARGET_NOT_FOUND',
  TARGET_SOFT_DELETED: 'tenant.processes.history.systemReason.TARGET_SOFT_DELETED',
  OTHER_BRANCH_ENDED: 'tenant.processes.history.systemReason.OTHER_BRANCH_ENDED',
  OTHER_BRANCH_ERRORED: 'tenant.processes.history.systemReason.OTHER_BRANCH_ERRORED',
  JOIN_WITHOUT_OPEN_BRANCHES: 'tenant.processes.history.systemReason.JOIN_WITHOUT_OPEN_BRANCHES',
  ESCALATED_TO_SUPERVISOR: 'tenant.processes.history.systemReason.ESCALATED_TO_SUPERVISOR',
  SUPERVISOR_NOTIFIED: 'tenant.processes.history.systemReason.SUPERVISOR_NOTIFIED',
};

/** Warianty zdań eskalacji BEZ nazwy przełożonego (brak `params.supervisorUserId`). */
export const ESCALATION_UNNAMED_I18N_KEY: Record<ProcessEscalationSystemReasonCode, string> = {
  ESCALATED_TO_SUPERVISOR: 'tenant.processes.history.systemReason.ESCALATED_TO_SUPERVISOR_UNNAMED',
  SUPERVISOR_NOTIFIED: 'tenant.processes.history.systemReason.SUPERVISOR_NOTIFIED_UNNAMED',
};

/** Wariant `OTHER_BRANCH_ENDED` z nazwą etapu końcowego (`params.endLabel`). */
export const OTHER_BRANCH_ENDED_NAMED_I18N_KEY =
  'tenant.processes.history.systemReason.OTHER_BRANCH_ENDED_NAMED';

/** Tekst ogólny („Wpis automatyczny") dla kodu powodu, którego ten panel nie zna. */
export const UNKNOWN_SYSTEM_REASON_I18N_KEY = 'tenant.processes.history.systemReason.UNKNOWN';

export interface SystemReasonTranslation {
  key: string;
  params: Record<string, string>;
}

function isKnownReasonCode(code: string): code is ProcessDecisionSystemReasonCode {
  return (PROCESS_DECISION_SYSTEM_REASON_CODES as readonly string[]).includes(code);
}

/**
 * Klucz i parametry tłumaczenia powodu. `OTHER_BRANCH_ENDED` z niepustą nazwą etapu końcowego
 * dostaje wariant z nazwą; bez nazwy (albo z samymi spacjami, albo bez `params`) — wariant
 * ogólny. Kody eskalacji (`PROCESS_ESCALATION_SYSTEM_REASON_CODES` z `@saas/shared`) z niepustym
 * `params.supervisorUserId` dostają zdanie z nazwą przełożonego policzoną przez `userLabelOf`;
 * bez identyfikatora albo gdy `userLabelOf` zwróci pustą (po `trim`) lub nietekstową nazwę —
 * wariant bez nazwy. Kod spoza znanej listy → tekst ogólny.
 *
 * Znane ograniczenie: przełożony spoza mapy nazw tabeli (`useUserLabels` zna pierwszych 100
 * użytkowników, a przy braku prawa podglądu użytkowników mapa jest pusta) pokazuje się w zdaniu
 * jako identyfikator — tak samo jak w kolumnie „Kto", bo obie drogi liczą nazwę tą samą funkcją.
 *
 * @param userLabelOf zamiana identyfikatora użytkownika na wyświetlaną nazwę — ta sama, której
 *   tabela używa w kolumnie „Kto" (z jej fallbackiem na identyfikator, gdy nazwy nie znamy).
 */
export function systemReasonTranslation(
  reason: ProcessDecisionSystemReason,
  userLabelOf: (userId: string) => string,
): SystemReasonTranslation {
  const code: string = reason.code;
  if (!isKnownReasonCode(code)) return { key: UNKNOWN_SYSTEM_REASON_I18N_KEY, params: {} };
  if (reason.code === 'OTHER_BRANCH_ENDED') {
    // `params` z jsonb bywa nieobecny albo ma inny kształt niż w typie — czytamy ostrożnie.
    const rawEndLabel: unknown = reason.params?.endLabel;
    const endLabel = typeof rawEndLabel === 'string' ? rawEndLabel.trim() : '';
    if (endLabel) return { key: OTHER_BRANCH_ENDED_NAMED_I18N_KEY, params: { endLabel } };
  }
  if (isProcessEscalationSystemReasonCode(code)) {
    const unnamed: SystemReasonTranslation = { key: ESCALATION_UNNAMED_I18N_KEY[code], params: {} };
    // Jak wyżej: identyfikator przełożonego z jsonb nie jest walidowany w czasie działania.
    // Strażnik kodu nie zawęża unii `reason` (zawęża tylko zmienną `code`), więc `params` czytamy
    // jako luźny słownik — taki, jaki faktycznie może przyjść z jsonb.
    const params: Readonly<Record<string, unknown>> | null | undefined = reason.params;
    const rawSupervisorUserId: unknown = params?.supervisorUserId;
    const supervisorUserId =
      typeof rawSupervisorUserId === 'string' ? rawSupervisorUserId.trim() : '';
    if (!supervisorUserId) return unnamed;
    // Nazwa z `userLabelOf` bywa pusta (np. użytkownik z pustą nazwą w mapie tabeli) — zdanie
    // z pustym miejscem po dwukropku byłoby gorsze niż zdanie bez nazwy.
    const rawSupervisorName: unknown = userLabelOf(supervisorUserId);
    const supervisor = typeof rawSupervisorName === 'string' ? rawSupervisorName.trim() : '';
    if (!supervisor) return unnamed;
    return { key: SYSTEM_REASON_I18N_KEY[code], params: { supervisor } };
  }
  return { key: SYSTEM_REASON_I18N_KEY[code], params: {} };
}
